import { ArrowDownward } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface AutoScrollContextValue {
  isAtBottom: boolean;
  isAutoScrolling: boolean;
  scrollElement: HTMLDivElement | null;
  scrollToBottom: () => void;
}

const AutoScrollContext = createContext<AutoScrollContextValue | null>(null);

export function useAutoScrollContext() {
  const context = useContext(AutoScrollContext);
  if (!context) {
    throw new Error(
      "useAutoScrollContext must be used within AutoScrollContainer"
    );
  }
  return context;
}

export function useOptionalAutoScrollContext() {
  return useContext(AutoScrollContext);
}

interface AutoScrollContainerProps {
  children: ReactNode;
  className?: string;
  /** Dependencies that trigger auto-scroll when changed (e.g., messages array) */
  dependencies?: unknown[];
  /** Threshold in pixels from bottom to consider "at bottom" */
  threshold?: number;
  /** Content to render in a fixed position layer (e.g., scroll-to-bottom button) */
  fixedContent?: ReactNode;
}

/**
 * Container component that provides auto-scroll behavior for chat interfaces.
 *
 * - Automatically scrolls to bottom when dependencies change (if user is at bottom)
 * - Stops auto-scrolling when user scrolls up
 * - Resumes auto-scrolling when user scrolls back to bottom
 */
export function AutoScrollContainer({
  children,
  className,
  dependencies = [],
  threshold = 50,
  fixedContent,
}: AutoScrollContainerProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null
  );
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const isUserScrolling = useRef(false);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout>>();
  const autoScrollTimeout = useRef<ReturnType<typeof setTimeout>>();
  const lastScrollTop = useRef(0);
  const isInitialMount = useRef(true);

  const handleScrollElement = useCallback((element: HTMLDivElement | null) => {
    scrollRef.current = element;
    setScrollElement(element);
  }, []);

  const checkIfAtBottom = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return true;

    const { scrollTop, scrollHeight, clientHeight } = element;

    // If content doesn't overflow, we're always "at bottom"
    if (scrollHeight <= clientHeight) return true;

    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    return distanceFromBottom <= threshold;
  }, [threshold]);

  const scrollToBottom = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    element.scrollTo({
      top: element.scrollHeight,
      behavior: "smooth",
    });
    setIsAtBottom(true);
  }, []);

  // Handle scroll events
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const handleScroll = () => {
      const currentScrollTop = element.scrollTop;

      // Detect if user is scrolling up (away from bottom)
      if (currentScrollTop < lastScrollTop.current) {
        isUserScrolling.current = true;
      }
      lastScrollTop.current = currentScrollTop;

      // Clear any existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      // Update isAtBottom state
      const atBottom = checkIfAtBottom();
      setIsAtBottom(atBottom);

      // If user scrolled back to bottom, reset the flag
      if (atBottom) {
        isUserScrolling.current = false;
      }

      // Reset the scrolling flag after scroll ends
      scrollTimeout.current = setTimeout(() => {
        // Only reset if at bottom
        if (checkIfAtBottom()) {
          isUserScrolling.current = false;
        }
      }, 150);
    };

    element.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      element.removeEventListener("scroll", handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [checkIfAtBottom]);

  // Auto-scroll when dependencies change (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only auto-scroll if user is at bottom and not actively scrolling up
    if (isAtBottom && !isUserScrolling.current) {
      setIsAutoScrolling(true);

      // Clear any existing auto-scroll timeout
      if (autoScrollTimeout.current) {
        clearTimeout(autoScrollTimeout.current);
      }

      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        const element = scrollRef.current;
        if (element) {
          element.scrollTo({
            top: element.scrollHeight,
            behavior: "smooth",
          });

          // Clear auto-scrolling flag after scroll animation completes
          autoScrollTimeout.current = setTimeout(() => {
            setIsAutoScrolling(false);
          }, 300);
        }
      });
    }

    return () => {
      if (autoScrollTimeout.current) {
        clearTimeout(autoScrollTimeout.current);
      }
    };
  }, dependencies);

  // Initial scroll to bottom
  useEffect(() => {
    requestAnimationFrame(() => {
      scrollToBottom();
    });
  }, []);

  const contextValue: AutoScrollContextValue = {
    isAtBottom,
    isAutoScrolling,
    scrollElement,
    scrollToBottom,
  };

  return (
    <AutoScrollContext.Provider value={contextValue}>
      <div ref={handleScrollElement} className={`relative ${className ?? ""}`}>
        {children}
        {fixedContent}
      </div>
    </AutoScrollContext.Provider>
  );
}

interface ScrollToBottomButtonProps {
  className?: string;
}

export function ScrollToBottomButton({ className }: ScrollToBottomButtonProps) {
  const { isAtBottom, isAutoScrolling, scrollToBottom } =
    useAutoScrollContext();

  // Don't show button if at bottom or if auto-scrolling is in progress
  if (isAtBottom || isAutoScrolling) return null;

  return (
    // <Button
    //   className={className}
    //   variant="contained"
    //   size="small"
    //   color="primary"
    //   onClick={scrollToBottom}
    //   // sx={{
    //   //   textTransform: "none",
    //   //   backgroundColor: "white",
    //   //   // add sligh tint on hover without making it transparent
    //   //   "&:hover": { backgroundColor: ")" },
    //   // }}
    //   endIcon={<ArrowDownward />}
    // >Follow output</Button>
    // Circular buton only showing the arrow down icon. Can't use mui button because it's not circular. Make it contained with a soft grey background and tint on hover.
    <IconButton
      className={`${className} rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-200 border-solid`}
      size="small"
      color="primary"
      onClick={scrollToBottom}
    >
      <ArrowDownward />
    </IconButton>
  );
}
